// @flow
import JsSHA from 'jssha';
import {is, when} from 'ramda';
import md5 from 'blueimp-md5';

const doHash = (email: string, sha: string = 'SHA-256'): string => {
    const shaEmail = new JsSHA(sha, 'TEXT');
    shaEmail.update(email);

    return shaEmail.getHash('HEX');
};

const transformers = {
    sha256: (email: string) => doHash(email.toLowerCase(), 'SHA-256'),
    sha1: (email: string) => doHash(email.toLowerCase(), 'SHA-1'),
    md5: (email: string) => md5(email),
    regionCode: (
        value: string,
        regions: Array<{id: string, label: string, value: string}>,
    ): string => {
        let newValue = '';
        regions.some((region: {label: string, value: string}): boolean => {
            if (region.value === value) {
                newValue = region.value;
                return true;
            }

            if (region.label === value) {
                newValue = region.value;
                return true;
            }

            return false;
        });

        return newValue;
    },
    regionId: (
        value: string,
        regions: Array<{id: string, label: string, value: string}>,
    ): string => {
        const filteredRegions = regions.filter(region => region.value === value);
        return filteredRegions.length > 0 ? filteredRegions[0].id : '';
    },
    checkCountry: (
        country: string,
        countries: Array<{value: string, label: string}>,
        defaultValue: string,
    ): string => (countries.some(item => item.value === country) ? country : defaultValue),
    /**
     * Transforms a number value to the prefixed float value.
     * If the received value is string, will be returned the same value.
     * @param {string} value - The value for transforming.
     * @returns {string} Formatted value.
     * @example
     *  toPrefixed(1); //=> +1.00
     *  toPrefixed(-5); //=> -5.00
     *  toPrefixed('string'); //=> string
     */
    toPrefixed: (value: number) =>
        when(
            is(Number),
            target => (target > 0 ? `+${target.toFixed(2)}` : target.toFixed(2)),
            value,
        ),
    /**
     * Transforms a number value to the padded integer value.
     * @param {string} value - The value for transforming.
     * @param {number} targetLength - The length of the resulting string.
     * @returns {string} Formatted value.
     * @example
     *  toPadded(1, 2); //=> 001
     *  toPadded('string'); //=> string
     */
    toPadded: (value: number, targetLength: number = 1) =>
        when(is(Number), target => target.toString().padStart(targetLength, 0), value),
};

/**
 * This method will transform string using an available transformers
 * @param {string} value Value for transform
 * @param {string} transformerName Name of transformer
 * @param {Array<any>} args Additional arguments
 * @return {string} value Transformed or plain value
 */
export function doTransform(value: string, transformerName: string, ...args: Array<any>): string {
    if (transformers[transformerName] && value !== '')
        return transformers[transformerName](value, ...args);
    return value;
}
